import React, { useState } from 'react';
import {
  TablePagination,
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import { JournalByDate, Order } from '../Statistics.types';
import { exportCSV } from '../utils/csvExport';
import { JournalsTableDesktop } from './JournalsTableDesktop';
import { JournalsTableMobile } from './JournalsTableMobile';

interface JournalsTableProps {
  sortedJournals: JournalByDate[];
  onSort: (property: keyof JournalByDate) => void;
  order?: Order;
  orderBy?: keyof JournalByDate;
}

export const JournalsTable: React.FC<JournalsTableProps> = ({
  sortedJournals,
  onSort,
  order = 'desc',
  orderBy = 'count',
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get paginated data
  const paginatedJournals = sortedJournals.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleDownload = () => {
    // Prepare data for TSV export
    const headers = [
      t('statistics.table.amount'),
      t('statistics.table.agreement'),
      t('statistics.table.name'),
      t('statistics.table.imprint'),
      t('statistics.table.payment'),
      t('statistics.table.publishingModel'),
    ];

    const rows = sortedJournals.map((journal) => [
      journal.count,
      journal.agreement_name || journal.agreement || '',
      journal.name || '',
      journal.imprint || '',
      journal.payment || '',
      journal.publishing_model || '',
    ]);

    const csvData = [headers, ...rows];
    exportCSV(csvData, t('statistics.journals.title', 'Journals'));
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        {/* Description */}
        <Typography variant="body2" sx={{ fontStyle: 'italic', flex: 1 }}>
          {t('statistics.help.journals')}
        </Typography>

        {/* Download button with same styling as StatisticsChart */}
        <ToggleButtonGroup
          exclusive
          size="small"
          aria-label={String(
            t('statistics.download.ariaLabel', 'Download options')
          )}
        >
          <Tooltip title={t('statistics.download', 'Download')}>
            <ToggleButton
              value="download"
              onClick={handleDownload}
              aria-label={String(t('statistics.download', 'Download'))}
              size="small"
            >
              <DownloadIcon sx={{ fontSize: 18 }} />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </Stack>
      {isMobile ? (
        <JournalsTableMobile journals={paginatedJournals} />
      ) : (
        <JournalsTableDesktop
          journals={paginatedJournals}
          onSort={onSort}
          order={order}
          orderBy={orderBy}
        />
      )}

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        sx={{
          borderTop: `1px solid var(--color-dark-greyish-blue)`,
        }}
        component="div"
        count={sortedJournals.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={t('statistics.table.rowsPerPage', 'Rows per page:')}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} ${t('statistics.table.of', 'of')} ${
            count !== -1 ? count : `more than ${to}`
          }`
        }
        SelectProps={{
          inputProps: {
            'aria-label': String(
              t('statistics.table.rowsPerPage', 'Rows per page')
            ),
            'aria-describedby': 'rows-per-page-helper',
            id: 'rows-per-page-select',
          },
          native: false, // Use the non-native select for better accessibility
        }}
      />
      <span
        id="rows-per-page-helper"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          borderWidth: 0,
        }}
      >
        {t(
          'statistics.table.rowsPerPageHelp',
          'Select the number of rows to display per page'
        )}
      </span>
    </Box>
  );
};
